/**
 * RecordSocket — the ONE WebSocket engine behind both providers
 * used by RecordScope.
 *
 * Framework-free: React never appears here. The providers own React state
 * and wire it up via the listener callbacks; everything race-sensitive —
 * the connect-token guard around the async auth-token fetch, reconnect
 * backoff, pending-request settlement, resubscribe-on-open, zombie-reconnect
 * prevention on teardown — lives here, under direct unit test
 * (__tests__/record-socket.test.ts).
 *
 * History: this logic used to exist twice (~250 lines each) and the copies
 * drifted three ways before being unified. Do not fork it again.
 */

import { recordMatchesWhere, reconnectDelayMs } from './record-matching'
import { RecordRoomNotReadyError, WriteCancelledError, WriteUnconfirmedError } from './errors'
import { parseServerError } from './serverErrors'
import type { CollectionSchema, Query } from '../../shared/types'
import type { RoomUser, RoomConnectionState, RecordData } from './types'
import { CONFIRMED_WRITE_RETRY_MS, MSG } from '../../shared/protocol/constants'
import { appOrigin } from '../platform/origin'

// WebSocket readyState per spec — avoids touching the global WebSocket
// object for constants (tests inject WebSocketImpl; node may lack a global).
const WS_CONNECTING = 0
const WS_OPEN = 1

/**
 * Liveness probe interval and response deadline. Ordinary quiet sockets send
 * a probe after 15 seconds and close only if that probe is unanswered for a
 * further 30 seconds.
 *
 * A peer that stops answering without closing leaves the connection
 * ESTABLISHED: no packets are lost, so nothing retransmits, so nothing ever
 * times out. Measured against a peer frozen mid-conversation, an unprobed
 * socket stayed open indefinitely — this is unbounded, not slow. That is a hung
 * Durable Object, a broken relay, or a blackholed path; `offline` does not fire
 * for any of them, because the machine's own network is fine.
 *
 * The probe costs nothing on the server: BaseRoom registers a
 * `WebSocketRequestResponsePair('ping','pong')`, so the Cloudflare runtime
 * answers it while the DO stays hibernated.
 */
const PING_EVERY_MS = 15_000
const PONG_TIMEOUT_MS = 30_000

/** The slice of RecordStore the engine writes to (structural, for tests). */
export interface RecordStoreLike {
  setQueryResult(queryKey: string, records: RecordData[]): void
  applyChange(
    queryKey: string,
    record: RecordData,
    changeType: 'create' | 'update' | 'delete',
  ): void
  hasRecord(queryKey: string, recordId: string): boolean
  setError(queryKey: string, error: string): void
  resetToLoading(queryKey: string): void
}

export interface RecordSocketListeners {
  onStatus(status: RoomConnectionState): void
  onReady(ready: boolean): void
  onRole(role: string | null): void
  onUsers(users: RoomUser[]): void
  /** MSG.LIST_SCHEMAS — optional for scopes that support schema discovery. */
  onSchemas?(schemas: CollectionSchema[]): void
  onPermissionError?(title: string, detail: string): void
  onValidationError?(title: string, detail: string): void
}

export interface RecordSocketConfig {
  roomId: string
  store: RecordStoreLike
  /** Fetch the auth token for the connection URL. Failures are swallowed —
   *  the socket connects tokenless and the server decides (anonymous rooms
   *  work; authed rooms reject). */
  getToken: () => Promise<string | null>
  listeners: RecordSocketListeners
  /** http(s)/ws(s) base; defaults to the app origin (the page origin in a browser). */
  wsUrl?: string
  /** Default '/ws'. */
  wsPathPrefix?: string
  /** Diagnostics hook (wsLog/debugLog per provider). */
  log?: (event: string, detail?: unknown) => void
  /** Test injection. Defaults to globalThis.WebSocket. */
  WebSocketImpl?: typeof WebSocket
  /**
   * Persistent registries, owned by the CALLER when the socket can be
   * recreated (auth identity change): hooks register subscriptions/handlers
   * once on mount, so they must survive a socket swap. Default to fresh
   * per-instance containers.
   */
  subscriptions?: Map<string, string>
  binaryHandlers?: Set<(data: ArrayBuffer) => void>
  yjsJoinHandlers?: Map<string, Set<(canWrite: boolean) => void>>
}

/**
 * A confirmed write waiting for the server's ACK.
 *
 * Writes survive a dropped connection: the outbox keeps them in the order
 * they were made and sends them again, under the same requestId, once the
 * reconnected room is ready. The room applies a requestId at most once and
 * answers a repeat with the first outcome (the confirmed-write ledger in
 * server/handlers/records.ts), so the ACK is always the write's real result.
 */
interface PendingWrite {
  message: { type: string; payload: Record<string, unknown> }
  resolve: (data?: unknown) => void
  reject: (error: Error) => void
  timeoutMs: number
  timer: ReturnType<typeof setTimeout> | null
  /** Sends the server left unanswered within `timeoutMs`; waiting while offline does not count. */
  unanswered: number
  /** Null until it first leaves this client; unsent offline work can still wait. */
  firstSentAt: { wall: number; monotonic: number } | null
}

/**
 * A write the server leaves unanswered on this many fresh connections fails.
 * Each unanswered send already closed the socket and reconnected, so the
 * connection is not the problem; waiting longer would hang the write forever.
 */
export const MAX_UNANSWERED_SENDS = 3

function randomPart(): string {
  return Math.random().toString(36).slice(2, 10).padEnd(8, '0')
}

export class RecordSocket {
  private ws: WebSocket | null = null
  private readonly subscriptions: Map<string, string> // subscriptionId → queryKey
  private readonly binaryHandlers: Set<(data: ArrayBuffer) => void>
  private readonly yjsJoinHandlers: Map<string, Set<(canWrite: boolean) => void>>
  /** requestId → write; Map order is send order. */
  private readonly outbox = new Map<string, PendingWrite>()
  /** The open socket finished its handshake (USER_INFO), so writes may go out. */
  private writable = false
  /** The room has been ready under the current identity; until then a write is refused, not queued. */
  private hasBeenReady = false
  private reconnectAttempt = 0
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private heartbeat: ReturnType<typeof setInterval> | null = null
  /** Any inbound frame proves the peer is answering. */
  private lastInboundAt = 0
  /** Monotonic time of the one probe awaiting an inbound answer. */
  private outstandingPingAt: number | null = null
  /** Monotonic time of the prior heartbeat callback. A long gap means the
   *  scheduler was suspended, so a pre-suspension probe cannot be judged. */
  private lastHeartbeatCheckAt = 0
  // Bumped by connect() (on entry) and disconnect()/destroy(). connect()
  // captures it before awaiting the auth token and re-checks after; a
  // mismatch means a teardown (or a newer connect) raced in during the
  // await, so the resolved connect must bail instead of leaking a socket.
  private connectToken = 0
  // Opaque identity the current socket was opened with (e.g. '' for
  // anonymous, the profile id once signed in). A connect() with a DIFFERENT
  // tag while OPEN forces a reconnect so the server sees the new identity.
  private identityTag: string | undefined
  private destroyed = false

  constructor(private readonly config: RecordSocketConfig) {
    this.subscriptions = config.subscriptions ?? new Map()
    this.binaryHandlers = config.binaryHandlers ?? new Set()
    this.yjsJoinHandlers = config.yjsJoinHandlers ?? new Map()
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────

  async connect(identityTag?: string): Promise<void> {
    if (this.destroyed) return
    const { config } = this
    const newIdentity = identityTag !== undefined && identityTag !== this.identityTag
    // Writes made as another identity never go out as this one.
    if (newIdentity && this.identityTag !== undefined) this.forgetIdentity()

    if (this.ws?.readyState === WS_OPEN) {
      if (newIdentity) {
        // Identity changed mid-connection (e.g. anonymous → signed-in): the
        // server derives identity from the connection's JWT, so reconnect.
        config.log?.('reconnecting with new identity', config.roomId)
        this.teardownSocket()
      } else {
        return
      }
    }
    if (this.ws?.readyState === WS_CONNECTING) return

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    const myToken = ++this.connectToken

    const params = new URLSearchParams()
    // Identity is derived server-side from the verified JWT — any
    // client-supplied identity params would be stripped at the worker edge,
    // so the token is the only identity we send.
    try {
      const token = await config.getToken()
      if (token) params.set('token', token)
    } catch {
      /* token fetch failed — connect tokenless; the server decides */
    }

    // A teardown or newer connect() raced in while we awaited the token —
    // bail so we don't open (and leak) a socket the caller already tore
    // down, whose onclose would then schedule a zombie reconnect.
    if (this.connectToken !== myToken || this.destroyed) return

    // Only an explicit tag overwrites the remembered identity — a bare
    // connect() (visibility retry, scheduled reconnect) keeps the last one.
    if (identityTag !== undefined) this.identityTag = identityTag

    // http(s) → ws(s); an explicit ws(s) base passes through unchanged.
    const baseUrl = (config.wsUrl ?? appOrigin()).replace(/^http/, 'ws')
    const pathPrefix = config.wsPathPrefix ?? '/ws'
    const url = `${baseUrl}${pathPrefix}/${config.roomId}?${params.toString()}`

    config.log?.('connecting', config.roomId)
    const WS = config.WebSocketImpl ?? WebSocket
    const ws = new WS(url)
    ws.binaryType = 'arraybuffer'
    this.ws = ws

    ws.onopen = () => {
      config.log?.('connected', config.roomId)
      config.listeners.onStatus('connected')
      this.startHeartbeat(ws)
      // Re-subscribe every active query. After a plain socket drop the
      // server has no memory of our subscriptions and sends no RESUBSCRIBE
      // (that only fires on membership changes) — without this, a
      // reconnected client silently stops receiving live updates.
      this.resubscribeAll(ws)
    }

    ws.onmessage = (event) => this.handleMessage(event)

    // Code-agnostic on purpose: EVERY close reconnects, including clean/1000
    // and server-initiated 1012 ('state-refresh', sent by
    // BaseRoom.disconnectAllSockets after an out-of-band server-side write).
    // We must NOT special-case "clean" closes as terminal — that would strand
    // a client on stale data after the server kicked it to force a resync.
    // The reconnect's onopen re-subscribes every active query, so fresh
    // QUERY_RESULTs replace the stale store contents (full resync).
    ws.onclose = () => {
      this.stopHeartbeat()
      config.log?.('disconnected', config.roomId)
      config.listeners.onStatus('disconnected')
      config.listeners.onReady(false)
      this.ws = null
      this.holdWrites()
      // Queries show loading (not silently-stale data) until the reconnect
      // resubscribes and fresh results land.
      for (const queryKey of this.subscriptions.values()) {
        config.store.resetToLoading(queryKey)
      }
      const attempt = this.reconnectAttempt
      const delay = reconnectDelayMs(attempt)
      config.log?.('scheduling reconnect', { roomId: config.roomId, attempt, delayMs: delay })
      this.reconnectAttempt = attempt + 1
      this.reconnectTimeout = setTimeout(() => void this.connect(this.identityTag), delay)
    }

    ws.onerror = () => {}
  }

  /**
   * Tear down and reset to a connectable state (auth identity change, scope
   * unmount-and-remount). Emits status 'connecting' so the UI shows progress
   * rather than a dead 'disconnected'.
   */
  disconnect(): void {
    this.connectToken++ // invalidate any in-flight connect() awaiting its token
    this.clearReconnect()
    this.teardownSocket()
    this.forgetIdentity()
    this.config.listeners.onStatus('connecting')
    this.config.listeners.onReady(false)
    this.reconnectAttempt = 0
  }

  /** Final teardown (unmount): no listener calls — the owner is gone. */
  destroy(): void {
    this.destroyed = true
    this.connectToken++
    this.clearReconnect()
    this.teardownSocket()
    this.dropWrites()
  }

  /** Zero the backoff (tab became visible, user asked to retry). */
  resetBackoff(): void {
    this.reconnectAttempt = 0
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WS_OPEN
  }

  private clearReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }

  /**
   * Turn a peer that stopped answering into a `close` event. That is the whole
   * job — `onclose` already reports 'disconnected', marks every query stale,
   * and reconnects with backoff. Without this it is never called, because
   * nothing about the connection is actually broken.
   */
  private startHeartbeat(ws: WebSocket): void {
    this.stopHeartbeat()
    this.lastInboundAt = performance.now()
    this.lastHeartbeatCheckAt = this.lastInboundAt
    this.outstandingPingAt = null
    this.heartbeat = setInterval(() => {
      if (ws.readyState !== WS_OPEN) return
      const now = performance.now()
      const callbackGap = now - this.lastHeartbeatCheckAt
      this.lastHeartbeatCheckAt = now
      if (this.outstandingPingAt !== null) {
        if (now - this.outstandingPingAt >= PONG_TIMEOUT_MS) {
          // The event loop can freeze after the runtime queued a pong but
          // before our message task runs. A heartbeat callback delayed by a
          // full response window cannot safely judge that old probe: send a
          // fresh one and give it the ordinary deadline after the scheduler
          // resumes. Normal 15-second callbacks still close a silent peer at
          // the original deadline.
          if (callbackGap >= PONG_TIMEOUT_MS) {
            ws.send('ping')
            this.outstandingPingAt = now
          } else {
            ws.close()
          }
        }
        return
      }
      // Only probe once the connection has actually gone quiet: an app
      // receiving live updates has already proved the peer is answering.
      if (now - this.lastInboundAt >= PING_EVERY_MS) {
        ws.send('ping')
        this.outstandingPingAt = now
      }
    }, PING_EVERY_MS)
    // A suspended timer's first resumed callback has no outstanding probe, so
    // it sends one instead of closing a healthy backgrounded connection.
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat)
    this.heartbeat = null
    this.outstandingPingAt = null
    this.lastHeartbeatCheckAt = 0
  }

  /** Close without firing onclose — prevents the zombie-reconnect. */
  private teardownSocket(): void {
    this.stopHeartbeat()
    this.holdWrites()
    const ws = this.ws
    if (!ws) return
    this.config.log?.('closing', this.config.roomId)
    ws.onclose = null
    ws.onmessage = null
    ws.onerror = null
    ws.onopen = null
    ws.close()
    this.ws = null
  }

  // ── sending ───────────────────────────────────────────────────────────────

  sendMessage(message: { type: string; payload: unknown }): void {
    if (this.ws?.readyState === WS_OPEN) this.ws.send(JSON.stringify(message))
    else this.config.log?.('send dropped (not connected)', message.type)
  }

  sendBinary(data: Uint8Array): void {
    if (this.ws?.readyState === WS_OPEN) this.ws.send(data)
  }

  /**
   * Sends a write and resolves with the server's ACK. Once the room has been
   * ready, a write made while the connection is down waits in the outbox and
   * goes out, in order, after the reconnect. A write the server does not
   * answer within `timeoutMs` marks the connection dead: the socket closes,
   * reconnects, and sends it again. Rejects when the server refuses the write;
   * with `WriteUnconfirmedError` when it leaves the write unanswered on
   * MAX_UNANSWERED_SENDS connections or a resend would outlive the server ledger;
   * with `RecordRoomNotReadyError` before
   * the room was ever ready; and with `WriteCancelledError` when the identity
   * changes or the socket is torn down before the write was sent.
   */
  sendConfirmed(
    message: { type: string; payload: Record<string, unknown> },
    timeoutMs = 10000,
  ): Promise<unknown> {
    if (this.destroyed) return Promise.reject(new WriteCancelledError())
    if (!this.hasBeenReady) return Promise.reject(new RecordRoomNotReadyError())
    // Unique per user for as long as the room's ledger remembers it (a day).
    const requestId = `${Date.now().toString(36)}-${randomPart()}${randomPart()}`
    return new Promise((resolve, reject) => {
      const write: PendingWrite = {
        message, resolve, reject, timeoutMs,
        timer: null, unanswered: 0, firstSentAt: null,
      }
      this.outbox.set(requestId, write)
      if (this.writable) this.transmit(requestId, write)
    })
  }

  private transmit(requestId: string, write: PendingWrite): void {
    const ws = this.ws
    if (!ws || ws.readyState !== WS_OPEN) return
    // Wall time includes device sleep on platforms whose monotonic clock pauses;
    // monotonic time prevents a backward system-clock correction extending retries.
    const now = { wall: Date.now(), monotonic: performance.now() }
    if (
      write.firstSentAt !== null &&
      Math.max(
        now.wall - write.firstSentAt.wall,
        now.monotonic - write.firstSentAt.monotonic,
      ) >= CONFIRMED_WRITE_RETRY_MS
    ) {
      this.outbox.delete(requestId)
      write.reject(new WriteUnconfirmedError())
      return
    }
    write.firstSentAt ??= now
    ws.send(JSON.stringify({ ...write.message, payload: { ...write.message.payload, requestId } }))
    write.timer = setTimeout(() => {
      write.timer = null
      write.unanswered += 1
      if (write.unanswered >= MAX_UNANSWERED_SENDS) {
        this.outbox.delete(requestId)
        write.reject(new WriteUnconfirmedError())
      }
      this.config.log?.('confirmation timed out; reconnecting', this.config.roomId)
      if (this.ws === ws) ws.close()
    }, write.timeoutMs)
  }

  /** The connection is gone: everything waits for the next handshake and is sent again. */
  private holdWrites(): void {
    this.writable = false
    for (const write of this.outbox.values()) {
      if (write.timer === null) continue
      clearTimeout(write.timer)
      write.timer = null
    }
  }

  private dropWrites(): void {
    for (const write of this.outbox.values()) {
      if (write.timer) clearTimeout(write.timer)
      write.reject(write.firstSentAt === null ? new WriteCancelledError() : new WriteUnconfirmedError())
    }
    this.outbox.clear()
  }

  /** Writes belong to the identity that made them; never send them as another. */
  private forgetIdentity(): void {
    this.dropWrites()
    this.hasBeenReady = false
  }

  private flushWrites(): void {
    this.writable = true
    this.hasBeenReady = true
    for (const [requestId, write] of this.outbox) {
      if (write.timer === null) this.transmit(requestId, write)
    }
  }

  // ── registrations ─────────────────────────────────────────────────────────

  registerSubscription(subscriptionId: string, queryKey: string): void {
    this.subscriptions.set(subscriptionId, queryKey)
  }

  unregisterSubscription(subscriptionId: string): void {
    this.subscriptions.delete(subscriptionId)
  }

  onBinaryMessage(handler: (data: ArrayBuffer) => void): () => void {
    this.binaryHandlers.add(handler)
    return () => {
      this.binaryHandlers.delete(handler)
    }
  }

  registerYjsJoinHandler(docKey: string, handler: (canWrite: boolean) => void): () => void {
    if (!this.yjsJoinHandlers.has(docKey)) this.yjsJoinHandlers.set(docKey, new Set())
    this.yjsJoinHandlers.get(docKey)!.add(handler)
    return () => {
      const handlers = this.yjsJoinHandlers.get(docKey)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) this.yjsJoinHandlers.delete(docKey)
      }
    }
  }

  // ── message dispatch ──────────────────────────────────────────────────────

  private resubscribeAll(ws: WebSocket): void {
    for (const [subscriptionId, queryKey] of this.subscriptions) {
      try {
        const query = JSON.parse(queryKey)
        ws.send(JSON.stringify({ type: MSG.SUBSCRIBE, payload: { subscriptionId, query } }))
      } catch {
        /* skip invalid query keys */
      }
    }
  }

  private handleMessage(event: MessageEvent): void {
    this.lastInboundAt = performance.now()
    this.outstandingPingAt = null
    // The runtime's auto-response to our probe. It carries no payload — its
    // only meaning is the timestamp above — and it is not JSON, so it must
    // return before the parse below.
    if (event.data === 'pong') return

    if (event.data instanceof ArrayBuffer) {
      this.binaryHandlers.forEach((h) => h(event.data as ArrayBuffer))
      return
    }

    let msg: { type: string; payload: unknown }
    try {
      msg = JSON.parse(event.data as string)
    } catch {
      console.error('[RecordSocket] Failed to parse message')
      return
    }

    const { type, payload } = msg
    const { store, listeners } = this.config

    switch (type) {
      case MSG.USER_INFO: {
        const { role } = payload as { role: string }
        listeners.onRole(role)
        listeners.onReady(true)
        this.reconnectAttempt = 0
        // Auto-request the user list as part of the connection handshake.
        this.sendMessage({ type: MSG.USER_LIST, payload: {} })
        this.flushWrites()
        break
      }

      case MSG.USER_LIST:
        listeners.onUsers((payload as { users: RoomUser[] }).users)
        break

      case MSG.QUERY_RESULT: {
        const { subscriptionId, records } = payload as {
          subscriptionId: string
          records: RecordData[]
        }
        const queryKey = this.subscriptions.get(subscriptionId)
        if (queryKey !== undefined) store.setQueryResult(queryKey, records)
        break
      }

      case MSG.RECORD_CHANGE: {
        const { collection, record, changeType } = payload as {
          collection: string
          record: RecordData
          changeType: 'create' | 'update' | 'delete'
        }
        for (const [subscriptionId, queryKey] of this.subscriptions) {
          try {
            const query = JSON.parse(queryKey) as Query
            if (query.collection !== collection) continue
            if (query.orderBy !== undefined || query.limit !== undefined) {
              this.sendMessage({
                type: MSG.SUBSCRIBE,
                payload: { subscriptionId, query },
              })
              continue
            }
            const matches = recordMatchesWhere(record, query.where)
            const exists = store.hasRecord(queryKey, record.recordId)
            if (changeType === 'delete') {
              if (exists) store.applyChange(queryKey, record, 'delete')
            } else if (changeType === 'create') {
              if (matches) store.applyChange(queryKey, record, 'create')
            } else {
              if (matches && exists) store.applyChange(queryKey, record, 'update')
              else if (matches && !exists) store.applyChange(queryKey, record, 'create')
              else if (!matches && exists) store.applyChange(queryKey, record, 'delete')
            }
          } catch {
            /* skip invalid query keys */
          }
        }
        break
      }

      case MSG.ERROR: {
        const { subscriptionId, error } = payload as { subscriptionId?: string; error: string }
        if (subscriptionId) {
          const queryKey = this.subscriptions.get(subscriptionId)
          if (queryKey !== undefined) store.setError(queryKey, error)
        } else {
          const parsed = parseServerError(error)
          if (parsed.isPermissionError) listeners.onPermissionError?.(parsed.title, parsed.detail)
          else listeners.onValidationError?.(parsed.title, parsed.detail)
        }
        break
      }

      case MSG.YJS_JOIN: {
        const { collection, recordId, fieldName, canWrite } = payload as {
          collection: string
          recordId: string
          fieldName: string
          canWrite: boolean
        }
        const docKey = `${collection}:${recordId}:${fieldName}`
        this.yjsJoinHandlers.get(docKey)?.forEach((h) => h(canWrite))
        break
      }

      case MSG.ACK: {
        const { requestId, success, error, ...rest } = payload as {
          requestId: string
          success: boolean
          error?: string
          [key: string]: unknown
        }
        const write = this.outbox.get(requestId)
        if (write) {
          if (write.timer) clearTimeout(write.timer)
          this.outbox.delete(requestId)
          if (success) write.resolve(rest)
          else write.reject(new Error(error || 'Mutation rejected'))
        }
        break
      }

      case MSG.LIST_SCHEMAS: {
        const { schemas } = payload as { schemas: CollectionSchema[] }
        listeners.onSchemas?.(schemas ?? [])
        break
      }

      case MSG.RESUBSCRIBE: {
        // Team membership changed — re-subscribe all active queries.
        if (this.ws?.readyState === WS_OPEN) this.resubscribeAll(this.ws)
        break
      }
    }
  }
}
